import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../routes";
import "./landing.scss";
import useScreenSize from "../../hooks/useScreenSize";

export default function LandingPage() {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleLogin = () => {
    navigate(ROUTES.PATHS.LOGIN);
  };

  const handleScrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (<div className="landing-page">
    <header>
      <div className="container">
        <nav>
          <a className="logo" href="#home">
            <span className="logo-dot"></span>
            <strong>SerialMan</strong>
            {!isMobile && <span style={{ opacity: 0.6, fontSize: "0.9rem" }}>Queue & Appointments</span>}
          </a>
          <div className="links">
            <a className="link" href="#features">Features</a>
            {!isMobile && <a className="link" href="#industries">Industries</a>}
            <a className="link" href="#pricing">Pricing</a>
            {!isMobile && <a className="link" href="#faq">FAQ</a>}
            {!isMobile && <a className="btn btn-outline" href="#demo">Request Demo</a> }
            <a className="link" onClick={handleLogin}>Login</a>
          </div>
        </nav>
      </div>
    </header>
    <section className="hero" style={{ paddingTop: "15px" }}>
      <div className="shadow-ring"></div>
      <div className="container hero-grid">
        <div>
          <span className="badge">Reduce wait time • Increase revenue • Delight customers</span>
          <h1>Automated Serials, Smart Queues & Online Appointments—built for Hospitals, Clinics & Salons</h1>
          <p className="lead">From doctor schedules to stylist slots, from walk‑in queues to online bookings—manage everything in one place. Real‑time tokens, reminders, payments, reports, and more.</p>
          <div className="cta">
            <a className="btn btn-primary" href="#pricing">Start Free Trial</a>
            <a className="btn" href="#demo">Book a Live Demo</a>
          </div>
          <div className="metrics">
            <div className="metric card">
              <b>40%↓</b>
              <div>Average waiting time</div>
            </div>
            <div className="metric card">
              <b>3x</b>
              <div>More completed bookings</div>
            </div>
            <div className="metric card">
              <b>99.9%</b>
              <div>Uptime SLA</div>
            </div>
            <div className="metric card">
              <b>GDPR/HIPAA</b>
              <div>Ready data protection</div>
            </div>
          </div>
        </div>

        <div className="hero-img card soft">
          <img alt="Product dashboard mockup" src="/serialman-app.png" />
        </div>
      </div>
    </section>
    <section className="section" id="features">
      <div className="container">
        <h2>Everything you need to run a smooth front‑desk</h2>
        <p className="sub">Powerful, flexible modules that work beautifully together or standalone.</p>

        <div className="grid cols-4">
          <div className="feature card">
            <div className="icon" aria-hidden="true">📅</div>
            <h3>Online & Walk‑in Appointments</h3>
            <p>Multiple providers & rooms, custom durations, buffer time, overbooking guard, slot limits, reschedule/cancel with policies.</p>
          </div>
          <div className="feature card">
            <div className="icon">🎟️</div>
            <h3>Smart Queue & Tokens</h3>
            <p>Live token screens, SMS/WhatsApp alerts, triage priority, no‑show recycle, and average wait ETA for each visitor.</p>
          </div>
          <div className="feature card">
            <div className="icon">👤</div>
            <h3>Customer / Patient CRM</h3>
            <p>Unified profiles, visit history, notes, allergies/preferences, attachments, and consent tracking.</p>
          </div>
          <div className="feature card">
            <div className="icon">🧾</div>
            <h3>Billing & Payments</h3>
            <p>POS, packages, discounts, taxes, online payments (Stripe/bKash/Nagad), and invoices with e‑receipt.</p>
          </div>
          <div className="feature card">
            <div className="icon">🔔</div>
            <h3>Reminders & Notifications</h3>
            <p>Automated SMS, email, and WhatsApp reminders; post‑visit feedback & NPS; missed‑call capture.</p>
          </div>
          <div className="feature card">
            <div className="icon">📊</div>
            <h3>Reports & Dashboards</h3>
            <p>Daily flow, utilization, revenue, cohort repeats, cancellation & no‑show trends—export to Excel/PDF.</p>
          </div>
          <div className="feature card">
            <div className="icon">🛡️</div>
            <h3>Security & Compliance</h3>
            <p>Role‑based access, audit logs, SSO with Microsoft/Google, encrypted at rest & in transit, data residency options.</p>
          </div>
          <div className="feature card">
            <div className="icon">⚙️</div>
            <h3>Integrations</h3>
            <p>Website widget, Facebook/Instagram, Google Calendar, Microsoft 365, HL7/FHIR (labs), Zapier, webhooks, REST API.</p>
          </div>
        </div>

        <div className="marquee" aria-label="Trusted by">
          <span className="pill">🏥 Multi‑specialty Hospitals</span>
          <span className="pill">🩺 Diagnostic & Imaging Centers</span>
          <span className="pill">💇 Salons & Barbers</span>
          <span className="pill">💅 Spas & Parlors</span>
          <span className="pill">👶 Pediatrics & Dental</span>
          <span className="pill success">PCI‑aware payments</span>
          <span className="pill warn">Queue screens & kiosks</span>
        </div>
      </div>
    </section>
    <section className="section" id="industries">
      <div className="container">
        <h2>Tailored flows for your industry</h2>
        <p className="sub">Pick a blueprint and go live in minutes—customize later as you grow.</p>

        <div className="grid cols-3">
          <div className="card feature">
            <h3>Hospitals & Clinics</h3>
            <p>OPD scheduling, department‑wise queues, on‑call consultant availability, patient categories, token displays, triage priority.</p>
          </div>
          <div className="card feature">
            <h3>Diagnostics & Labs</h3>
            <p>Sample collection slots, device utilization, fasting reminders, report ready alerts, HL7/FHIR interfaces.</p>
          </div>
          <div className="card feature">
            <h3>Salons, Spas & Parlors</h3>
            <p>Multi‑service combos, stylist preferences, chair/room allocation, packages & memberships, add‑on upsells.</p>
          </div>
        </div>
      </div>
    </section>
    <section className="section" id="pricing">
      <div className="container">
        <h2>Simple, transparent pricing</h2>
        <p className="sub">Start free. Upgrade when you&#8217;re ready. No setup fees, cancel anytime.</p>

        <div className="pricing">
          <div className="plan">
            <div className="badge" style={{ background: "rgba(255,255,255,.06)" }}>Starter</div>
            <h3>For solo providers & small teams</h3>
            <div className="price">$19 <small>/month</small></div>
            <ul>
              <li>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20.3 7.7l-1.4-1.4z" /></svg>
                1 location, up to 3 staff
              </li>
              <li><svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20.3 7.7l-1.4-1.4z" /></svg> Online bookings & queue</li>
              <li><svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M9 16.2l-3.5-3.5-1.4 1.4L9 19 20.3 7.7l-1.4-1.4z" /></svg> Email reminders</li>
            </ul>
            <p><a className="btn" href="#demo">Try free</a></p>
          </div>

          <div className="plan pop">
            <div className="badge" style={{ background: "rgba(255,255,255,.2)" }}>Professional</div>
            <h3>Growing multi‑provider teams</h3>
            <div className="price">$49 <small>/month</small></div>
            <ul>
              <li>3 locations, up to 20 staff</li>
              <li>SMS/WhatsApp reminders</li>
              <li>Payments & invoicing</li>
              <li>Reports & exports</li>
              <li>Basic API</li>
            </ul>
            <p><a className="btn btn-outline" href="#demo">Start now</a></p>
          </div>

          <div className="plan">
            <div className="badge" style={{ background: "rgba(255,255,255,.06)" }}>Enterprise</div>
            <h3>Hospitals, chains & call centers</h3>
            <div className="price">Let&#8217;s talk</div>
            <ul>
              <li>Unlimited locations & staff</li>
              <li>SSO, audit logs, RBAC</li>
              <li>HL7/FHIR, custom workflows</li>
              <li>Premium SLA & onboarding</li>
              <li>Private cloud & data residency</li>
            </ul>
            <p><a className="btn" href="#contact">Contact sales</a></p>
          </div>
        </div>
      </div>
    </section>

    <section className="section" id="socialproof">
      <div className="container">
        <h2>What customers say</h2>
        <p className="sub">Real results from real teams.</p>

        <div className="testi">
          <div className="quote">
            <p>“Our OPD wait time dropped from 1h+ to <strong>20 minutes</strong>. Doctors love the clean schedule view.”</p>
            <p className="name">— Admin, CityCare Hospital</p>
          </div>
          <div className="quote">
            <p>“No‑shows are down 35% after automated WhatsApp reminders. Revenue is up.”</p>
            <p className="name">— Owner, Shine Salon</p>
          </div>
          <div className="quote">
            <p>“The token screen + triage priority made our reception super efficient.”</p>
            <p className="name">— Manager, Prime Diagnostics</p>
          </div>
        </div>
      </div>
    </section>

    <section className="section" id="demo">
      <div className="container">
        <div className="card" style={{ padding: "24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr .8fr", gap: "18px", alignItems: "center" }}>
            <div>
              <h2>See it in action</h2>
              <p className="sub">Book a 20‑minute walkthrough. We&#8217;ll map your current flow and set up a free trial for your team.</p>
              <ul>
                <li>Migration from spreadsheets or existing tools</li>
                <li>Branding with your logo, colors & SMS sender ID</li>
                <li>Role‑based access for doctors, stylists & front‑desk</li>
              </ul>
            </div>
            <form id="contact" className="card" style={{ padding: "18px", background: "linear-gradient(180deg,rgba(255,255,255,.08),rgba(255,255,255,.04))" }}>
              <label className="sr" htmlFor="name">Name</label>
              <input id="name" name="name" placeholder="Full name" required style={{ width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.06)", color: "#fff;margin-bottom:10px" }} />
              <label className="sr" htmlFor="email">Email</label>
              <input id="email" type="email" name="email" placeholder="Work email" required style={{ width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.06)", color: "#fff", marginBottom: "10px" }} />
              <label className="sr" htmlFor="company">Company</label>
              <input id="company" name="company" placeholder="Company / Clinic" style={{ width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.06)", color: "#fff", marginBottom: "10px" }} />
              <label className="sr" htmlFor="industry">Industry</label>
              <select id="industry" name="industry" style={{ width: "100%", padding: "12px 14px", borderRadius: "12px", border: "1px solid rgba(255,255,255,.18)", background: "rgba(255,255,255,.06)", color: "#fff", marginBottom: "10px" }}>
                <option>Hospital/Clinic</option>
                <option>Diagnostics/Lab</option>
                <option>Salon/Barber</option>
                <option>Spa/Parlor</option>
                <option>Other</option>
              </select>
              <button className="btn btn-primary" type="submit">Request Demo</button>
              <p style={{ opacity: .7, fontSize: ".85rem", marginTop: "10px" }}>By submitting you agree to our <a href="/terms">Terms</a> and <a href="/privacy-policy">Privacy</a>.</p>
            </form>
          </div>
        </div>
      </div>
    </section>

    <section className="section" id="faq">
      <div className="container faq">
        <h2>Frequently asked questions</h2>
        <p className="sub">Have another question? <a href="#contact">Contact sales</a>.</p>

        <details>
          <summary>Can I use both appointments and queue tokens together?</summary>
          <div>Yes. Walk‑ins join the live queue, while online bookings get reserved slots. Our algorithm blends both so providers are never idle.</div>
        </details>
        <details>
          <summary>Do you support local SMS gateways (e.g., bKash/Nagad in Bangladesh)?</summary>
          <div>Yes. We integrate with global providers like Twilio as well as regional gateways on request. bKash/Nagad payments are supported.</div>
        </details>
        <details>
          <summary>Is my data secure and compliant?</summary>
          <div>Data is encrypted in transit and at rest. We provide RBAC, audit logs, SSO, IP whitelisting, backups, and options aligned with GDPR/HIPAA practices.</div>
        </details>
        <details>
          <summary>How fast can I go live?</summary>
          <div>Most teams go live the same week. Import your staff & services, connect SMS, and add our booking widget to your website/Facebook page.</div>
        </details>
      </div>
    </section>

    <footer>
      <div className="container footer-grid">
        <div>
          <div className="logo" style={{ marginBottom: "10px" }}><span className="logo-dot"></span><strong>HrnSoft</strong></div>
          <p style={{ opacity: .85, maxWidth: "560px" }}>Queue & Appointment Automation for hospitals, clinics, diagnostics, salons, and wellness brands. Reduce wait times, fill schedules, and create outstanding customer experiences.</p>
        </div>
        <div>
          <h4>Product</h4>
          <ul className="footer-links">
            <li><a href="#features">Features</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li><a href="#demo">Demo</a></li>
          </ul>
        </div>
        <div>
          <h4>Company</h4>
          <ul className="footer-links">
            <li><a href="#">About</a></li>
            <li><a href="#">Careers</a></li>
            <li><a href="#">Blog</a></li>
          </ul>
        </div>
        <div>
          <h4>Legal</h4>
          <ul className="footer-links">
            <li><a href="/terms">Terms</a></li>
            <li><a href="/privacy-policy">Privacy</a></li>
            <li><a href="#">Security</a></li>
          </ul>
        </div>
      </div>
      <div className="container" style={{ marginTop: "18px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
        <div className="copyright">© <span id="y"></span> HrnSoft. All rights reserved.</div>
        <div style={{ opacity: .7 }}>Made with ❤ for fast front‑desks.</div>
      </div>
    </footer>

    {showScrollTop && (
      <button 
        className="scroll-to-top"
        onClick={handleScrollTop}
        title="Scroll to top"
        aria-label="Scroll to top"
      >
        ↑
      </button>
    )}
  </div>
  );
}
